import React, { useRef, useState } from 'react';
import IntlTelInput from 'react-intl-tel-input';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import parsePhoneNumber, {
  CountryCode,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import { DRAWER_Z_INDEX } from '../../definitions/constants';

export interface IntlTelInputFieldProps extends InputBaseComponentProps {
  onlyCountries?: string[];
}

interface PhoneFieldProps extends IntlTelInputFieldProps {
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  fixedHeight?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  label?: string;
  loading?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  required?: boolean;
  size?: 'small' | 'medium';
  value: any;
  dataTestId?: string;
}

const fieldStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(1.25)} 0`,
    margin: `0 ${theme.spacing(0.75)}`,
    width: '100%',
    '& .country-list': {
      position: 'fixed',
      zIndex: DRAWER_Z_INDEX + 1,
    },
  },
  input: {
    border: 'none',
    outline: 'none',
    width: '100%',
  },
}));

/**
 * react-intl-tel-input wrapper to mix it with @mui
 * @see https://github.com/patw0929/react-intl-tel-input/blob/master/src/components/IntlTelInput.js
 * @param props
 *
 * @warn OutlinedInput.inputComponent expects an HTMLInputElement but it is incompatible with IntlTelInput.
 *       That's why there's an unused `inputRef` argument and a eslint exception for that
 */
const IntlTelInputField = React.forwardRef<
  HTMLInputElement,
  IntlTelInputFieldProps
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>((props, inputRef) => {
  const {
    autoFocus,
    disabled,
    onBlur,
    onChange,
    onFocus,
    onlyCountries,
    value,
  } = props;
  const ref = useRef<IntlTelInput>(null);
  const classes = fieldStyles();
  const nationalMode = true;
  const numberType = 'MOBILE';
  const preferredCountries = !onlyCountries?.length ? ['us'] : [];

  // get country from international code
  const getDefaultCountry = () => {
    const parsedPhone = parsePhoneNumber(value ?? '', 'US');
    return parsedPhone?.country?.toLowerCase() || 'us';
  };

  const [defaultCountry] = useState(getDefaultCountry());
  const [isFocus, setIsFocus] = useState(false);

  // Callback for handling change events at IntlTelInput
  // eslint-disable-next-line func-names
  const handleChange = function (
    currentNumber: string,
    countryIso: string | undefined,
    optionalOnChange?: React.ChangeEventHandler<HTMLInputElement>
  ) {
    const country = countryIso || (onlyCountries && onlyCountries[0]);

    /**
     * With useUtils.getExamples, the phone numbers given for some countries were
     * shorter, so when a longer, valid number was inserted, it would not parse correctly.
     * With validatePhoneNumberLength we are able to validate the max length
     * possible for that country code and check if the inserted number is too long.
     */
    const validLength =
      validatePhoneNumberLength(
        currentNumber,
        country?.toUpperCase() as CountryCode
      ) === 'TOO_LONG'
        ? currentNumber.length - 1
        : undefined;
    // Trim number to the mask maximum length
    const number = currentNumber.slice(0, validLength);
    // Manually transform the full number using the trimmed number (not the param)
    const r = ref.current;
    const fullNumber = r ? (r as any).formatFullNumber(number) : number;
    // react-intl-tel-input does not returns the event
    // so it is faken and pass into the onChange handler
    const event = {
      target: {
        value: { fullNumber, number } as unknown,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    if (optionalOnChange) {
      optionalOnChange(event);
    }
  };

  const handleBlur = (
    a: any,
    b: any,
    c: any,
    d: any,
    f: any,
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    setIsFocus(false);
    if (onBlur) {
      onBlur(e);
    }
  };
  const handleFocus = (
    a: any,
    b: any,
    c: any,
    d: any,
    f: any,
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    setIsFocus(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  return (
    <IntlTelInput
      ref={ref}
      autoFocus={autoFocus}
      containerClassName={`intl-tel-input ${classes.root}`}
      customPlaceholder={(placeholder: string) =>
        // Hide the input placeholder on focus because some users get confused
        isFocus ? '' : placeholder
      }
      defaultCountry={defaultCountry}
      disabled={disabled}
      format
      formatOnInit
      onlyCountries={onlyCountries}
      inputClassName={classes.input}
      nationalMode={nationalMode}
      numberType={numberType}
      onPhoneNumberBlur={handleBlur}
      onPhoneNumberFocus={handleFocus}
      onPhoneNumberChange={(
        valid: boolean,
        currentNumber: string,
        country: any
      ) => {
        handleChange(currentNumber, country.iso2, onChange);
      }}
      onSelectFlag={(
        currentNumber: string,
        country: any,
        fullNumber: string,
        isValid: boolean
      ) => {
        // It's better to clenaup the value, but only we know the value is invalid for sure
        handleChange(
          !!window.intlTelInputUtils && !isValid ? '' : currentNumber,
          country.iso2,
          onChange
        );
      }}
      preferredCountries={preferredCountries}
      value={value}
      useMobileFullscreenDropdown={false}
    />
  );
});

const componentStyles = makeStyles((theme) => ({
  label: {
    background: 'white',
    padding: `0 ${theme.spacing(0.625)}`,
    marginLeft: -theme.spacing(0.625),
  },
  mediumSize: {
    padding: theme.spacing(1),
  },
}));

const PhoneField = React.forwardRef<HTMLDivElement, PhoneFieldProps>(
  (props: PhoneFieldProps, ref) => {
    const classes = componentStyles();

    const {
      autoFocus,
      className,
      disabled = false,
      error = false,
      fixedHeight = false,
      fullWidth = false,
      helperText,
      label,
      loading,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onlyCountries = [],
      placeholder,
      required = false,
      size = 'small',
      value,
      dataTestId,
    } = props;

    const internalRef = useRef(null);

    return (
      <FormControl
        className={className}
        error={error}
        ref={ref}
        required={required}
        size={size}
        variant="outlined"
        fullWidth={fullWidth}
        data-testid={dataTestId}
      >
        {label && (
          <InputLabel className={classes.label} color="secondary" shrink>
            {label}
          </InputLabel>
        )}
        <OutlinedInput
          ref={internalRef}
          className={classNames({ [classes.mediumSize]: size === 'medium' })}
          color="secondary"
          disabled={disabled}
          endAdornment={
            loading && <CircularProgress color="inherit" size={20} />
          }
          inputComponent={IntlTelInputField}
          inputProps={{
            autoFocus,
            onBlur,
            onFocus,
            onlyCountries,
            placeholder,
          }}
          notched
          onClick={(e: React.MouseEvent<HTMLInputElement>) => {
            // ref can't be bypass down to the IntlTelInputField
            // so lets use the DOM to focus the input field
            const node = internalRef.current as any;
            if (node) {
              const input = node.children[0].children[1];
              input.focus();
            }

            if (onClick) {
              // trick for when this field is used in an autocomplete, in order to
              // open the autocomplete options
              e.currentTarget = e.target as any;
              onClick(e);
            }
          }}
          onChange={onChange}
          sx={(theme) => ({
            height: fixedHeight ? theme.spacing(7) : undefined,
          })}
          value={value}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default PhoneField;
